import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CiudadesService } from 'src/app/services/ciudades.service';
import { EventosService } from 'src/app/services/eventos.service';
import { CropperImageComponent } from '../../bloques/cropper-image/cropper-image.component';
import { BusinessService } from '../../../services/business.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';

@Component({
  selector: 'app-create-company',
  templateUrl: './create-company.component.html',
  styleUrls: ['./create-company.component.scss'],
})
export class CreateCompanyComponent implements OnInit {
  cities: any[] = [];
  states: any[] = [];
  countries: any[] = [];

  ivaOptions = [
    { id: 1, name: 'IVA General (21%)' },
    { id: 2, name: 'IVA Reducido (10%)' },
    { id: 3, name: 'IVA Super Reducido (4%)' },
    { id: 4, name: 'Sin IVA (0%)' },
  ];

  feeTypes = [
    { id: 1, label: 'Porcentaje', icon: 'fa-percent', desc: 'Comisión sobre el precio (ej. 10%)' },
    { id: 2, label: 'Fija',       icon: 'fa-euro-sign', desc: 'Importe fijo por entrada (ej. 1,50€)' },
    { id: 3, label: 'Por tramos', icon: 'fa-layer-group', desc: 'Importe variable según el precio' },
    { id: 4, label: 'Gratuito',   icon: 'fa-ban', desc: 'Sin comisión' },
  ];

  // The last tier is always the open one (max: null = no limit)
  // Array starts with one limited tier + one open tier
  feeTiers: { max: number | null; value: number | null }[] = [
    { max: null, value: null },  // limited tier (editable)
    { max: null, value: null },  // open tier ∞ (always last)
  ];

  user: any = this.authenticationService.getProfile();

  companyForm: FormGroup;
  showErrorMsg: boolean = false;
  currentStep: number = 1;
  loading: boolean = false;

  constructor(
    private authenticationService: AuthenticationService,
    private businessService: BusinessService,
    private ciudadesService: CiudadesService,
    private dialogRef: MatDialogRef<CreateCompanyComponent>,
    public dialog: MatDialog,
  ) {
    this.dialogRef.disableClose = true;
    this.companyForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      fiscal_name: new FormControl('', [Validators.required]),
      cif: new FormControl('', [Validators.required]),
      address: new FormControl('', [Validators.required]),
      postal_code: new FormControl('', [
        Validators.required,
        Validators.maxLength(5),
      ]),
      description: new FormControl(''),
      general_conditions: new FormControl(''),
      city: new FormControl('', [Validators.required]),
      id_state: new FormControl('', [Validators.required]),
      id_country: new FormControl('', [Validators.required]),
      imgControl: new FormControl(''),
      file: new FormControl(''),
      cropped: new FormControl(''),
      taxes: new FormControl('', [Validators.required]),
      // Fee fields
      fee_type: new FormControl(1),
      fee_value: new FormControl(null, [Validators.required, Validators.min(0)]),

    });
  }

  ngOnInit() {
    this.ciudadesService.getCiudades().subscribe({
      next: (data) => {
        this.cities = data.data;
      },
    });

    this.ciudadesService.getCountries().subscribe({
      next: (data) => {
        this.countries = data.data;
      },
    });

    this.ciudadesService.getStates().subscribe({
      next: (data) => {
        this.states = data.data;
      },
    });

    // Validación dinámica de fee_value según el tipo seleccionado
    this.companyForm.get('fee_type')!.valueChanges.subscribe((type: number) => {
      const feeValueCtrl = this.companyForm.get('fee_value')!;
      if (type === 1 || type === 2) {
        feeValueCtrl.setValidators([Validators.required, Validators.min(0)]);
      } else {
        feeValueCtrl.clearValidators();
      }
      feeValueCtrl.updateValueAndValidity();
    });
  }

  createCompany() {
    if (this.companyForm.valid) {
      const form = this.companyForm.value;

      // Build fee
      const feeType = form.fee_type ?? 1;
      // "Gratuito" (id=4) se simula como tipo Fija (id=2) con value=0
      const apiFeeType = feeType === 4 ? 2 : feeType;
      const feeLabels: Record<number, string> = { 1: 'Porcentaje', 2: 'Fijo', 3: 'Variable', 4: 'Gratis' };
      const feeName = `${feeLabels[feeType]} - ${form.name}`;
      // Los valores introducidos por el usuario incluyen IVA (21%).
      // La API almacena el valor sin IVA → dividir entre 1.21, redondeo al alza con 2 decimales.
      const removeVat = (v: number) => Math.round((v / 1.21) * 100) / 100;
      let valueFee: any[];
      if (feeType === 4) {
        valueFee = [{ value: 0 }];
      } else if (feeType === 3) {
        valueFee = this.feeTiers
          .filter(t => t.value !== null && t.value !== undefined)
          .map(t => ({ max: t.max ?? 9999, value: removeVat(t.value!) }));
      } else {
        valueFee = [{ value: removeVat(form.fee_value ?? 0) }];
      }

      const slug = form.name
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // quitar acentos
        .replace(/[^a-z0-9\s-]/g, '')                    // solo letras, números, espacios y guiones
        .trim()
        .replace(/\s+/g, '-');                            // espacios → guiones

      const payload: any = {
        id_user: this.user.id,
        name: form.name,
        slug,
        cif: form.cif,
        fiscal_name: form.fiscal_name,
        address: form.address,
        postal_code: form.postal_code.toString(),
        city: form.city,
        id_state: form.id_state,
        id_country: form.id_country,
        id_tax: form.taxes,
        description: form.description ? String(form.description).trim() : '',
        general_conditions: form.general_conditions ? String(form.general_conditions).trim() : '',
        fee: {
          id_type_fee: apiFeeType,
          name: feeName,
          value: valueFee,
          is_percentage: apiFeeType === 1,
        },
      };

      if (form.file) payload.file = form.file;
      this.loading = true;
      this.businessService.createBussiness(payload).subscribe({
        next: (data) => {
          this.loading = false;
          if (data.success) {
            this.dialogRef.close(data.data);
          } else {
            this.dialog.open(InfoCardComponent, {
              panelClass: 'info-dialog',
              data: {
                icon: true,
                text: 'Error al crear la empresa',
              },
            });
          }
        },
        error: (error) => {
          this.loading = false;
          this.dialog.open(InfoCardComponent, {
            panelClass: 'info-dialog',
            data: {
              icon: true,
              text: 'Error al crear la empresa',
            },
          });
        },
      });
    } else {
      this.showErrorMsg = true;
      this.companyForm.markAllAsTouched();
    }
  }

  close() {
    this.dialogRef.close();
  }

  confirmClose() {
    const ref = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: true,
        text: '¿Seguro que quieres salir? Se perderán los datos introducidos.',
        btnCancel: true,
        textButton: 'Salir',
        warning: true,
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dialogRef.close();
      }
    });
  }

  nextStep() {
    if (this.currentStep === 1) {
      // Validar campos del paso 1 (Datos Fiscales)
      const fiscalFields = [
        'fiscal_name',
        'cif',
        'address',
        'postal_code',
        'id_country',
        'id_state',
        'city',
        'taxes',
      ];
      let isValid = true;

      fiscalFields.forEach((field) => {
        const control = this.companyForm.get(field);
        if (control && control.invalid) {
          control.markAsTouched();
          isValid = false;
        }
      });

      if (isValid) {
        this.currentStep = 2;
        this.showErrorMsg = false;
      } else {
        this.showErrorMsg = true;
      }
    } else if (this.currentStep === 2) {
      const step2Fields = ['name'];
      let isValid = true;
      step2Fields.forEach((field) => {
        const control = this.companyForm.get(field);
        if (control && control.invalid) {
          control.markAsTouched();
          isValid = false;
        }
      });
      if (isValid) {
        this.currentStep = 3;
        this.showErrorMsg = false;
      } else {
        this.showErrorMsg = true;
      }
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.showErrorMsg = false;
    }
  }

  addTier() {
    if (!this.tiersAreValid()) return;
    // Insert new tier before the last one (the open ∞ tier)
    this.feeTiers.splice(this.feeTiers.length - 1, 0, { max: null, value: null });
  }

  tiersAreValid(): boolean {
    // All limited tiers (all except last) must have max > 0 and max > prev max
    for (let i = 0; i < this.feeTiers.length - 1; i++) {
      const max = this.feeTiers[i].max;
      if (max === null || max === undefined || max <= 0) return false;
      if (i > 0 && max <= (this.feeTiers[i - 1].max ?? 0)) return false;
    }
    return true;
  }

  tierIsInvalid(i: number): boolean {
    if (i >= this.feeTiers.length - 1) return false; // last (∞) is always valid
    const max = this.feeTiers[i].max;
    if (max === null || max === undefined || max <= 0) return false; // empty — no error yet
    if (i > 0 && max <= (this.feeTiers[i - 1].max ?? 0)) return true;
    return false;
  }

  removeTier(i: number) {
    // Min 2 tiers: 1 limited + 1 open. Never delete the last (∞) one.
    if (this.feeTiers.length > 2 && i < this.feeTiers.length - 1) {
      this.feeTiers.splice(i, 1);
    }
  }

  onPostalCodeInput(event: any) {
    const input = event.target.value;
    if (input.length > 5) {
      event.target.value = input.slice(0, 5);
      this.companyForm.patchValue({ postal_code: input.slice(0, 5) });
    }
  }

  async onFileChange(event: any) {
    let files = event.target.files;

    if (files && files.length) {
      let file = files[0];

      // Validación de tipo de archivo
      if (!file.type.match('image.*')) {
        this.dialog.open(InfoCardComponent, {
          panelClass: 'info-dialog',
          data: {
            icon: true,
            text: 'Solo se permite utilizar imágenes.',
            btnCancel: false,
          },
        });
        event.target.value = '';
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10 MB

      // Comprimir la imagen de forma segura
      try {
        file = await this.compressImage(file);
      } catch (e) {
        console.error('Error comprimiendo:', e);
      }

      // Validar el peso final después de la compresión
      if (file.size > maxSize) {
        this.dialog.open(InfoCardComponent, {
          panelClass: 'info-dialog',
          data: {
            icon: true,
            text: 'La imagen no puede pesar más de 10 MB.',
            btnCancel: false,
          },
        });
        event.target.value = '';
        return;
      }

      // Crear el evento falso para no romper el DOM ni el Cropper
      const fakeEvent = {
        target: {
          files: [file],
        },
      };

      const dialogRef = this.dialog.open(CropperImageComponent, {
        panelClass: 'info-dialog',
        disableClose: true,
        data: fakeEvent,
      });

      dialogRef.afterClosed().subscribe((result) => {
        event.target.value = ''; // Limpiamos el input
        if (result) {
          this.companyForm.controls.cropped.setValue(result.pathUrl);
          this.companyForm.patchValue({
            file: result.object.base64,
            cropped: this.companyForm.controls.cropped.value,
          });
          this.companyForm.controls.imgControl.setValue(
            this.companyForm.controls.cropped.value,
          );
        }
      });
    }

    if (!event.target.files || event.target.files.length === 0) {
      this.companyForm.controls['file'].reset();
      this.companyForm.controls['cropped'].reset();
    }
  }

  async compressImage(file: File): Promise<File> {
    return new Promise((resolve) => {
      if (!file.type.match(/image\/(jpeg|png|webp)/)) {
        resolve(file);
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const MAX_WIDTH = 1920;
        const MAX_HEIGHT = 1080;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.7,
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };
    });
  }
}
